import React, { useEffect, useState } from "react"
import { Button, Card, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import recordNotFound from "../../../assets/images/record_not_found.png"
import PageTitleButton from "../../../components/PageTitleButton"
import SweetAlerts from "../../../components/SweetAlerts"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import {
  useHandleUnauthorizedAccess,
  HasModulePermission,
} from "../../../lib/GlobalMethods"
import AmenitiesSkeleton from "../../../skeleton/settings/AmenitiesSkeleton"
import AddEditAmenity from "./AddEditAmenity"
import { modulePermission } from "../../../lib/GlobalVariables"

const AmenitiesManagement = () => {
  const canEdit = HasModulePermission(modulePermission.setting, "edit")
  const canDelete = HasModulePermission(modulePermission.setting, "delete")
  const canAdd = HasModulePermission(modulePermission.setting, "add")

  const [show, setShow] = useState(false)
  const [data, setData] = useState([])
  const [isUpdate, setIsUpdate] = useState(false)
  const [isDelete, setIsDelete] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [loading, setLoading] = useState(false)

  const { handleAccess } = useHandleUnauthorizedAccess()

  const handleShow = () => setShow(true)

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setShowAlert(false)
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.amenityList,
      })
      handleAccess({ code: response?.data?.code })
      const data = response?.data?.data
      data?.sort((value, nextValue) => nextValue.id - value.id)
      setData(data)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error)
    }
  }

  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: "DELETE",
        endpoint: admin.deleteAmenity + id,
      })

      if (response?.data?.code === 200) {
        toast.success(response.data.message)
        setData((prevData) => prevData.filter((amenity) => amenity.id !== id))
        setData((data) =>
          data.sort((value, nextValue) => nextValue.id - value.id)
        )
        setLoading(false)
      } else {
        setLoading(false)
        throw new Error(response?.data?.message)
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <>
      {canAdd ? (
        <PageTitleButton
          PagetitleText={"Amenities Management"}
          HeaderButtonText={"Add Amenity"}
          IconclassName={"plus_black"}
          onClick={handleShow}
        />
      ) : (
        <PageTitleButton
          PagetitleText={"Amenities Management"}
          onClick={handleShow}
        />
      )}

      {!loading ? (
        <Card>
          <Card.Body>
            <Table className="rounded table-striped" responsive="md">
              <thead>
                <tr>
                  <th className="text-center">Logo</th>
                  <th className="text-center">Name</th>
                  {(canEdit || canDelete) && (
                    <th className="text-center">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data &&
                  data?.length > 0 &&
                  data?.map((data, index) => (
                    <tr key={index + 1}>
                      <td className="text-center">
                        <img
                          src={
                            data.amenity_img
                              ? process.env.REACT_APP_RESOURCE_URL +
                                data.amenity_img
                              : "https://placehold.co/50x50.png"
                          }
                          alt={data.amenity_name}
                          title={data.amenity_name}
                          className="amenity_img"
                        />
                      </td>
                      <td className="text-center">{data.amenity_name}</td>

                      {(canEdit || canDelete) && (
                        <td className="text-center">
                          <div className="d-inline-flex gap-2">
                            {canEdit && (
                              <TooltipComponent
                                toolplace="top"
                                tooltipText="Edit"
                              >
                                <Button
                                  variant="btnedit_icon"
                                  className="btn_icon"
                                  onClick={() => {
                                    handleShow()
                                    setIsUpdate(data)
                                  }}
                                >
                                  <i className="icon_main edit_pencil"></i>
                                </Button>
                              </TooltipComponent>
                            )}
                            {canDelete && (
                              <TooltipComponent
                                toolplace="top"
                                tooltipText="Delete"
                              >
                                <Button
                                  variant="btndelete_icon"
                                  className="btn_icon"
                                  onClick={() => {
                                    setShowAlert(true)
                                    setIsUpdate(data)
                                  }}
                                >
                                  <i className="icon_main delete_trash"></i>
                                </Button>
                              </TooltipComponent>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                {(data?.length === 0 || data?.length === undefined) && (
                  <tr>
                    <td className="text-center py-4" colSpan={3}>
                      <div className="norecord_wrapper">
                        <img src={recordNotFound} alt="No Record Found" />
                        <p className="norecords_title">No Records Found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <AmenitiesSkeleton />
      )}

      <AddEditAmenity
        show={show}
        setShow={setShow}
        handleClose={handleClose}
        fetchData={fetchData}
        setIsUpdate={setIsUpdate}
        isUpdate={isUpdate}
        setData={setData}
        setIsDelete={setIsDelete}
        setShowAlert={setShowAlert}
      />

      <SweetAlerts
        showAlert={showAlert}
        loading={loading}
        alertMessage={"Are you sure you want to delete this Amenity ?"}
        handleClose={handleClose}
        iconClassName={"delete_trash"}
        handleAlert={() => handleDelete(isUpdate.id)}
      />
    </>
  )
}

export default AmenitiesManagement
